// src/types.ts
var WebcvtError = class extends Error {
  code;
  constructor(code, message, options) {
    super(message, options);
    this.name = "WebcvtError";
    this.code = code;
  }
};
var UnsupportedFormatError = class extends WebcvtError {
  // `hint` is an optional third arg appended as an extra sentence. Additive: the
  // existing 2-arg call sites keep their exact message (no trailing hint).
  constructor(format, direction, hint) {
    super(
      "UNSUPPORTED_FORMAT",
      `Unsupported ${direction} format: "${format}". Install an additional @catlabtech/webcvt-* package or check the format name.${hint ? ` ${hint}` : ""}`
    );
    this.name = "UnsupportedFormatError";
  }
};
var NoBackendError = class extends WebcvtError {
  constructor(input, output) {
    super(
      "NO_BACKEND",
      `No backend can convert ${input} \u2192 ${output}. Install a matching @catlabtech/webcvt-backend-* or @catlabtech/webcvt-codec-* package.`
    );
    this.name = "NoBackendError";
  }
};

// src/errors.ts
var InputTooLargeError = class extends WebcvtError {
  constructor(codePrefix, formatLabel, size, max) {
    super(
      `${codePrefix}_INPUT_TOO_LARGE`,
      `${formatLabel} input is ${size} bytes; maximum supported is ${max} bytes (200 MiB).`
    );
    this.name = "InputTooLargeError";
  }
};
var EncodeNotImplementedError = class extends WebcvtError {
  constructor(codePrefix, message) {
    super(`${codePrefix}_ENCODE_NOT_IMPLEMENTED`, message);
    this.name = "EncodeNotImplementedError";
  }
};

// src/round-trip-backend.ts
var RoundTripBackend = class {
  name;
  config;
  constructor(config) {
    this.name = config.name;
    this.config = config;
  }
  async canHandle(input, output) {
    if (!this.config.mimes.has(input.mime)) return false;
    if (this.config.acceptsOutput) return this.config.acceptsOutput(input, output);
    if (this.config.canHandleMode === "strict-identity") return input.mime === output.mime;
    return this.config.mimes.has(output.mime);
  }
  async convert(input, output, options) {
    const startMs = Date.now();
    const { sizeGuard } = this.config;
    if (sizeGuard && input.size > sizeGuard.maxBytes) {
      throw sizeGuard.error(input.size, sizeGuard.maxBytes);
    }
    const { demuxStep, serializeStep } = this.config;
    options.onProgress?.({ percent: demuxStep.percent, phase: demuxStep.phase });
    const inputBytes = new Uint8Array(await input.arrayBuffer());
    const parsed = this.config.parse(inputBytes);
    options.onProgress?.({ percent: serializeStep.percent, phase: serializeStep.phase });
    if (this.config.mimes.has(output.mime)) {
      const outputBytes = await this.config.serialize(parsed);
      options.onProgress?.({ percent: 100, phase: "done" });
      const blob = new Blob([outputBytes.buffer], {
        type: this.config.outputMime ?? output.mime
      });
      return {
        blob,
        format: output,
        durationMs: Date.now() - startMs,
        backend: this.name,
        hardwareAccelerated: false
      };
    }
    throw this.config.encodeNotImplemented(output);
  }
};

// src/formats.ts
var KNOWN_FORMATS = [
  // Image (Phase 1)
  { ext: "png", mime: "image/png", category: "image", description: "Portable Network Graphics" },
  { ext: "jpeg", mime: "image/jpeg", category: "image", description: "JPEG" },
  { ext: "webp", mime: "image/webp", category: "image", description: "Web Picture" },
  { ext: "bmp", mime: "image/bmp", category: "image", description: "Bitmap" },
  { ext: "ico", mime: "image/x-icon", category: "image", description: "Windows Icon" },
  { ext: "gif", mime: "image/gif", category: "image", description: "Graphics Interchange Format" },
  // Image (modern codecs — jsquash wasm backends)
  {
    ext: "avif",
    mime: "image/avif",
    category: "image",
    description: "AV1 Image File Format (AVIF)"
  },
  { ext: "jxl", mime: "image/jxl", category: "image", description: "JPEG XL (JXL)" },
  // Image (HEIC/HEIF — libheif wasm backend, decode-only)
  {
    ext: "heic",
    mime: "image/heic",
    category: "image",
    description: "High Efficiency Image Container (HEIC)"
  },
  {
    ext: "heif",
    mime: "image/heif",
    category: "image",
    description: "High Efficiency Image Format (HEIF)"
  },
  // Subtitle (Phase 1)
  { ext: "srt", mime: "application/x-subrip", category: "subtitle", description: "SubRip" },
  { ext: "vtt", mime: "text/vtt", category: "subtitle", description: "WebVTT" },
  {
    ext: "ass",
    mime: "text/x-ass",
    category: "subtitle",
    description: "Advanced SubStation Alpha"
  },
  { ext: "ssa", mime: "text/x-ssa", category: "subtitle", description: "SubStation Alpha" },
  { ext: "sub", mime: "text/x-microdvd", category: "subtitle", description: "MicroDVD / VobSub" },
  { ext: "mpl", mime: "text/x-mpl2", category: "subtitle", description: "MPL2" },
  // Audio/Video registered for format-detection even though backends arrive in later phases
  { ext: "aac", mime: "audio/aac", category: "audio", description: "Advanced Audio Coding (ADTS)" },
  { ext: "mp3", mime: "audio/mpeg", category: "audio", description: "MPEG Audio Layer III" },
  { ext: "wav", mime: "audio/wav", category: "audio", description: "Waveform Audio File" },
  { ext: "ogg", mime: "audio/ogg", category: "audio", description: "Ogg" },
  { ext: "flac", mime: "audio/flac", category: "audio", description: "Free Lossless Audio Codec" },
  { ext: "mp4", mime: "video/mp4", category: "video", description: "MPEG-4 Part 14" },
  { ext: "m4a", mime: "audio/mp4", category: "audio", description: "MP4 audio (AAC-in-M4A)" },
  { ext: "webm", mime: "video/webm", category: "video", description: "WebM" },
  { ext: "mkv", mime: "video/x-matroska", category: "video", description: "Matroska container" },
  { ext: "ts", mime: "video/mp2t", category: "video", description: "MPEG-2 Transport Stream" },
  // Additional audio/video containers & codecs handled by backend-wasm (ffmpeg).
  // Descriptors live here (not privately in backend-wasm) so capability packages
  // single-source them via findByMime() and never drift.
  { ext: "mov", mime: "video/quicktime", category: "video", description: "QuickTime Movie" },
  { ext: "avi", mime: "video/x-msvideo", category: "video", description: "Audio Video Interleave" },
  { ext: "flv", mime: "video/x-flv", category: "video", description: "Flash Video" },
  { ext: "3gp", mime: "video/3gpp", category: "video", description: "3GPP" },
  { ext: "wmv", mime: "video/x-ms-wmv", category: "video", description: "Windows Media Video" },
  { ext: "f4v", mime: "video/x-f4v", category: "video", description: "Flash MP4 Video" },
  { ext: "opus", mime: "audio/opus", category: "audio", description: "Opus Audio" },
  { ext: "wma", mime: "audio/x-ms-wma", category: "audio", description: "Windows Media Audio" },
  {
    ext: "aiff",
    mime: "audio/aiff",
    category: "audio",
    description: "Audio Interchange File Format"
  },
  // Image (Phase 4: image-animation package — animated variants)
  { ext: "apng", mime: "image/apng", category: "image", description: "Animated PNG (APNG)" },
  // Image (Phase 4: image-svg package)
  { ext: "svg", mime: "image/svg+xml", category: "image", description: "Scalable Vector Graphics" },
  // Data-text (Phase 4: data-text package)
  {
    ext: "json",
    mime: "application/json",
    category: "data",
    description: "JavaScript Object Notation"
  },
  { ext: "csv", mime: "text/csv", category: "data", description: "Comma-Separated Values" },
  {
    ext: "tsv",
    mime: "text/tab-separated-values",
    category: "data",
    description: "Tab-Separated Values"
  },
  { ext: "ini", mime: "text/x-ini", category: "data", description: "INI Configuration File" },
  {
    ext: "env",
    mime: "text/plain",
    category: "data",
    description: "Environment Variables File (.env)"
  },
  {
    ext: "jsonl",
    mime: "application/jsonl",
    category: "data",
    description: "JSON Lines"
  },
  {
    ext: "toml",
    mime: "application/toml",
    category: "data",
    description: "Tom's Obvious Minimal Language"
  },
  {
    ext: "fwf",
    mime: "text/plain",
    category: "data",
    description: "Fixed-Width Format"
  },
  {
    ext: "xml",
    mime: "application/xml",
    category: "data",
    description: "Extensible Markup Language"
  },
  {
    ext: "yaml",
    mime: "application/yaml",
    category: "data",
    description: "YAML Aint Markup Language 1.2 Core"
  },
  // Image-legacy (Phase 4: image-legacy package)
  {
    ext: "pbm",
    mime: "image/x-portable-bitmap",
    category: "image",
    description: "Portable Bitmap (Netpbm PBM)"
  },
  {
    ext: "pgm",
    mime: "image/x-portable-graymap",
    category: "image",
    description: "Portable Graymap (Netpbm PGM)"
  },
  {
    ext: "ppm",
    mime: "image/x-portable-pixmap",
    category: "image",
    description: "Portable Pixmap (Netpbm PPM)"
  },
  {
    ext: "pfm",
    mime: "image/x-portable-floatmap",
    category: "image",
    description: "Portable Float Map (Netpbm PFM)"
  },
  { ext: "qoi", mime: "image/qoi", category: "image", description: "Quite OK Image Format" },
  {
    ext: "tiff",
    mime: "image/tiff",
    category: "image",
    description: "Tag Image File Format (TIFF 6.0)"
  },
  { ext: "tga", mime: "image/x-tga", category: "image", description: "Truevision TGA (Targa)" },
  { ext: "xbm", mime: "image/x-xbitmap", category: "image", description: "X11 Bitmap" },
  { ext: "pcx", mime: "image/x-pcx", category: "image", description: "PC Paintbrush" },
  { ext: "xpm", mime: "image/x-xpixmap", category: "image", description: "X PixMap" },
  { ext: "icns", mime: "image/icns", category: "image", description: "Apple Icon Image" },
  // Legacy / specialized image formats handled by backend-wasm (ffmpeg, identity-only).
  {
    ext: "psd",
    mime: "image/vnd.adobe.photoshop",
    category: "image",
    description: "Adobe Photoshop"
  },
  { ext: "blp", mime: "image/x-blp", category: "image", description: "BLP Texture" },
  { ext: "dds", mime: "image/vnd.ms-dds", category: "image", description: "DirectDraw Surface" },
  {
    ext: "eps",
    mime: "application/postscript",
    category: "image",
    description: "Encapsulated PostScript"
  },
  { ext: "jp2", mime: "image/jp2", category: "image", description: "JPEG 2000" },
  // Document (image-pdf package — wraps images into a PDF)
  {
    ext: "pdf",
    mime: "application/pdf",
    category: "document",
    description: "Portable Document Format"
  },
  // Document (doc-ebook-epub package — read-only EPUB reader)
  {
    ext: "epub",
    mime: "application/epub+zip",
    category: "document",
    description: "EPUB Electronic Publication (EPUB 3 / OCF)"
  },
  // Document (comic package — CBZ → PDF; CBR/CB7 detected, decode deferred)
  {
    ext: "cbz",
    mime: "application/vnd.comicbook+zip",
    category: "document",
    description: "Comic Book ZIP archive (CBZ)"
  },
  {
    ext: "cbr",
    mime: "application/vnd.comicbook-rar",
    category: "document",
    description: "Comic Book RAR archive (CBR)"
  },
  {
    ext: "cb7",
    mime: "application/x-cb7",
    category: "document",
    description: "Comic Book 7z archive (CB7)"
  },
  // Email (email package — RFC 5322 / MIME message parsing)
  {
    ext: "eml",
    mime: "message/rfc822",
    category: "email",
    description: "Email Message (RFC 5322 / MIME)"
  },
  // Font (font package — sfnt (TTF/OTF) ↔ WOFF 1.0 repackaging)
  { ext: "ttf", mime: "font/ttf", category: "font", description: "TrueType Font (sfnt)" },
  { ext: "otf", mime: "font/otf", category: "font", description: "OpenType Font (sfnt/CFF)" },
  { ext: "woff", mime: "font/woff", category: "font", description: "Web Open Font Format 1.0" },
  // Archive (Phase 4: archive-zip package)
  {
    ext: "zip",
    mime: "application/zip",
    category: "archive",
    description: "ZIP Archive (stored + Deflate)"
  },
  {
    ext: "tar",
    mime: "application/x-tar",
    category: "archive",
    description: "POSIX ustar TAR Archive"
  },
  // gz is the canonical entry for application/gzip MIME (findByMime returns this)
  { ext: "gz", mime: "application/gzip", category: "archive", description: "GZip Compressed File" },
  {
    ext: "bz2",
    mime: "application/x-bzip2",
    category: "archive",
    description: "bzip2 Compressed File (backend-wasm required)"
  },
  {
    ext: "xz",
    mime: "application/x-xz",
    category: "archive",
    description: "XZ Compressed File (backend-wasm required)"
  }
];
var EXT_ALIASES = {
  jpg: "jpeg",
  // .tgz is an alias for .gz (both are gzip-compressed; the tar layer is detected by content)
  tgz: "gz",
  // .yml is an alias for .yaml
  yml: "yaml"
};
var BY_EXT = new Map(
  KNOWN_FORMATS.map((f) => [f.ext.toLowerCase(), f])
);
var BY_MIME = new Map(
  KNOWN_FORMATS.map((f) => [f.mime.toLowerCase(), f])
);
function findByExt(ext) {
  const normalized = ext.toLowerCase().replace(/^\./, "");
  const canonical = EXT_ALIASES[normalized] ?? normalized;
  return BY_EXT.get(canonical);
}
function findByMime(mime) {
  return BY_MIME.get(mime.toLowerCase());
}
function resolveFormat(input) {
  if (typeof input !== "string") return input;
  if (input.includes("/")) return findByMime(input);
  return findByExt(input);
}
function knownFormats() {
  return KNOWN_FORMATS;
}

// src/detect.ts
var SIGNATURES = [
  { ext: "png", offset: 0, bytes: [137, 80, 78, 71, 13, 10, 26, 10] },
  { ext: "jpg", offset: 0, bytes: [255, 216, 255] },
  { ext: "webp", offset: 0, bytes: [82, 73, 70, 70] },
  // RIFF — check 'WEBP' at 8 separately
  { ext: "bmp", offset: 0, bytes: [66, 77] },
  { ext: "ico", offset: 0, bytes: [0, 0, 1, 0] },
  { ext: "gif", offset: 0, bytes: [71, 73, 70, 56] },
  // 'GIF8' (7a or 9a)
  { ext: "mp4", offset: 4, bytes: [102, 116, 121, 112] },
  // 'ftyp'
  { ext: "webm", offset: 0, bytes: [26, 69, 223, 163] },
  // EBML, shared with MKV
  { ext: "wav", offset: 0, bytes: [82, 73, 70, 70] },
  // RIFF — check 'WAVE' at 8 separately
  { ext: "ogg", offset: 0, bytes: [79, 103, 103, 83] },
  { ext: "mp3", offset: 0, bytes: [73, 68, 51] },
  // ID3v2
  { ext: "flac", offset: 0, bytes: [102, 76, 97, 67] },
  { ext: "zip", offset: 0, bytes: [80, 75, 3, 4] },
  // CBR (Comic Book RAR): "Rar!\x1a\x07" — shared by RAR4 and RAR5 (and plain .rar).
  { ext: "cbr", offset: 0, bytes: [82, 97, 114, 33, 26, 7] },
  // CB7 (Comic Book 7z): "7z\xbc\xaf\x27\x1c" (and plain .7z).
  { ext: "cb7", offset: 0, bytes: [55, 122, 188, 175, 39, 28] },
  { ext: "gz", offset: 0, bytes: [31, 139] },
  { ext: "bz2", offset: 0, bytes: [66, 90, 104] },
  // 'BZh'
  { ext: "xz", offset: 0, bytes: [253, 55, 122, 88, 90, 0] },
  // TAR ustar: magic "ustar\0" at offset 257 (POSIX 1003.1).
  // HEADER_BYTES_TO_READ must be >= 263 to reach this offset.
  { ext: "tar", offset: 257, bytes: [117, 115, 116, 97, 114, 0] },
  // "ustar\0"
  // QOI: 4-byte magic "qoif" at offset 0 (image/qoi)
  { ext: "qoi", offset: 0, bytes: [113, 111, 105, 102] },
  // WOFF 1.0: "wOFF" at offset 0.
  { ext: "woff", offset: 0, bytes: [119, 79, 70, 70] },
  // OpenType/CFF sfnt: "OTTO" at offset 0.
  { ext: "otf", offset: 0, bytes: [79, 84, 84, 79] },
  // TrueType sfnt: version 0x00010000 at offset 0 (does NOT collide with ICO "00 00 01 00").
  { ext: "ttf", offset: 0, bytes: [0, 1, 0, 0] },
  // Apple legacy TrueType sfnt: "true" at offset 0.
  { ext: "ttf", offset: 0, bytes: [116, 114, 117, 101] }
];
var HEADER_BYTES_TO_READ = 1024;
var SVG_SCAN_BYTES = 1024;
function matchesAt(buf, offset, bytes) {
  if (buf.length < offset + bytes.length) return false;
  for (let i = 0; i < bytes.length; i += 1) {
    if (buf[offset + i] !== bytes[i]) return false;
  }
  return true;
}
var EPUB_MIMETYPE_NAME = [109, 105, 109, 101, 116, 121, 112, 101];
var EPUB_MIMETYPE_VALUE = [
  97,
  112,
  112,
  108,
  105,
  99,
  97,
  116,
  105,
  111,
  110,
  47,
  101,
  112,
  117,
  98,
  43,
  122,
  105,
  112
];
function isEpubOcf(buf) {
  return matchesAt(buf, 30, EPUB_MIMETYPE_NAME) && matchesAt(buf, 38, EPUB_MIMETYPE_VALUE);
}
function detectSvgFromBytes(buf) {
  const first = buf[0];
  if (first === void 0) return void 0;
  const isLikelyText = first === 60 || // '<'
  first === 32 || // space
  first === 9 || // tab
  first === 10 || // LF
  first === 13 || // CR
  first === 239 || // UTF-8 BOM byte 1
  first === 255 || // UTF-16 BOM — handled by replacement decode
  first === 254;
  if (!isLikelyText) return void 0;
  const sliceLen = Math.min(buf.length, SVG_SCAN_BYTES);
  const text = new TextDecoder("utf-8", { fatal: false }).decode(buf.subarray(0, sliceLen));
  const stripped = text.replace(/^\uFEFF/, "");
  const afterXml = stripped.replace(/^<\?xml[^?]*\?>\s*/i, "");
  const afterComments = afterXml.replace(/^(<!--[\s\S]*?-->\s*)*/g, "");
  const trimmed = afterComments.trimStart();
  if (trimmed.startsWith("<svg") || trimmed.startsWith("<SVG")) {
    return findByExt("svg");
  }
  return void 0;
}
function detectNetpbmFromBytes(buf) {
  if (buf.length < 2) return void 0;
  if ((buf[0] ?? 0) !== 80) return void 0;
  const b1 = buf[1] ?? 0;
  switch (b1) {
    case 49:
    // '1'
    case 52:
      return findByExt("pbm");
    case 50:
    // '2'
    case 53:
      return findByExt("pgm");
    case 51:
    // '3'
    case 54:
      return findByExt("ppm");
    case 102:
    // 'f'
    case 70:
      return findByExt("pfm");
  }
  return void 0;
}
function disambiguateRiff(buf) {
  if (buf.length < 12) return void 0;
  const fourcc = String.fromCharCode(buf[8] ?? 0, buf[9] ?? 0, buf[10] ?? 0, buf[11] ?? 0);
  if (fourcc === "WEBP") return "webp";
  if (fourcc === "WAVE") return "wav";
  return void 0;
}
function disambiguateFtyp(buf) {
  const boxSize = ((buf[0] ?? 0) << 24 | (buf[1] ?? 0) << 16 | (buf[2] ?? 0) << 8 | (buf[3] ?? 0)) >>> 0;
  const end = Math.min(boxSize > 8 ? boxSize : buf.length, buf.length);
  let sawHeif = false;
  for (let off = 8; off + 4 <= end; off += 4) {
    if (off === 12) continue;
    const brand = String.fromCharCode(
      buf[off] ?? 0,
      buf[off + 1] ?? 0,
      buf[off + 2] ?? 0,
      buf[off + 3] ?? 0
    );
    if (brand === "avif" || brand === "avis") return "avif";
    if (brand === "heic" || brand === "heix" || brand === "heim" || brand === "heis") return "heic";
    if (brand === "mif1" || brand === "msf1" || brand === "heif") sawHeif = true;
  }
  return sawHeif ? "heif" : "mp4";
}
function disambiguatePng(buf) {
  let offset = 8;
  while (offset + 12 <= buf.length) {
    const length = ((buf[offset] ?? 0) << 24 | (buf[offset + 1] ?? 0) << 16 | (buf[offset + 2] ?? 0) << 8 | (buf[offset + 3] ?? 0)) >>> 0;
    const type = String.fromCharCode(
      buf[offset + 4] ?? 0,
      buf[offset + 5] ?? 0,
      buf[offset + 6] ?? 0,
      buf[offset + 7] ?? 0
    );
    if (type === "acTL") return "apng";
    if (type === "IDAT" || type === "IEND") break;
    offset += 4 + 4 + length + 4;
  }
  return "png";
}
async function detectFormat(input) {
  const head = input instanceof Uint8Array ? input.subarray(0, HEADER_BYTES_TO_READ) : new Uint8Array(await input.slice(0, HEADER_BYTES_TO_READ).arrayBuffer());
  if (matchesAt(head, 0, [82, 73, 70, 70])) {
    const kind = disambiguateRiff(head);
    if (kind) return findByExt(kind);
  }
  for (const sig of SIGNATURES) {
    if (sig.ext === "webp" || sig.ext === "wav") continue;
    if (matchesAt(head, sig.offset, sig.bytes)) {
      if (sig.ext === "png") return findByExt(disambiguatePng(head));
      if (sig.ext === "mp4") return findByExt(disambiguateFtyp(head));
      if (sig.ext === "zip" && isEpubOcf(head)) return findByExt("epub");
      return findByExt(sig.ext);
    }
  }
  if (head[0] === 71 && head.length >= 189 && head[188] === 71) {
    return findByExt("ts");
  }
  if (head[4] === 71 && head.length >= 197 && head[196] === 71) {
    return findByExt("ts");
  }
  {
    const netpbmResult = detectNetpbmFromBytes(head);
    if (netpbmResult) return netpbmResult;
  }
  {
    const svgResult = detectSvgFromBytes(head);
    if (svgResult) return svgResult;
  }
  if (head[0] === 255) {
    const b1 = head[1];
    if (b1 !== void 0 && (b1 === 251 || b1 === 250 || b1 === 243 || b1 === 242)) {
      return findByExt("mp3");
    }
  }
  if (head[0] === 255) {
    const b1 = head[1];
    if (b1 !== void 0 && (b1 & 240) === 240) {
      const lowNibble = b1 & 15;
      if (lowNibble === 0 || lowNibble === 1 || lowNibble === 8 || lowNibble === 9) {
        return findByExt("aac");
      }
    }
  }
  return void 0;
}
async function detectFormatWithHint(input, filenameHint) {
  const byMagic = await detectFormat(input);
  if (byMagic) return byMagic;
  if (filenameHint) {
    const dot = filenameHint.lastIndexOf(".");
    if (dot >= 0) {
      return findByExt(filenameHint.slice(dot + 1));
    }
  }
  return void 0;
}

// src/capability.ts
function hasGlobal(name) {
  try {
    return typeof globalThis[name] !== "undefined";
  } catch {
    return false;
  }
}
function detectCapabilities() {
  return {
    webCodecs: hasGlobal("VideoEncoder") || hasGlobal("AudioEncoder"),
    videoEncoder: hasGlobal("VideoEncoder"),
    videoDecoder: hasGlobal("VideoDecoder"),
    audioEncoder: hasGlobal("AudioEncoder"),
    audioDecoder: hasGlobal("AudioDecoder"),
    offscreenCanvas: hasGlobal("OffscreenCanvas"),
    compressionStream: hasGlobal("CompressionStream"),
    decompressionStream: hasGlobal("DecompressionStream"),
    webWorker: hasGlobal("Worker"),
    sharedArrayBuffer: hasGlobal("SharedArrayBuffer")
  };
}

// src/registry.ts
var BackendRegistry = class {
  backends = [];
  register(backend) {
    if (this.backends.some((b) => b.name === backend.name)) {
      throw new Error(`Backend "${backend.name}" is already registered`);
    }
    this.backends.push(backend);
  }
  unregister(name) {
    const idx = this.backends.findIndex((b) => b.name === name);
    if (idx < 0) return false;
    this.backends.splice(idx, 1);
    return true;
  }
  list() {
    return [...this.backends];
  }
  async findFor(input, output) {
    const ordered = [...this.backends].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
    for (const backend of ordered) {
      if (await backend.canHandle(input, output)) return backend;
    }
    return void 0;
  }
};
var defaultRegistry = new BackendRegistry();

// src/convert.ts
async function convert(input, options, context = {}) {
  const registry = context.registry ?? defaultRegistry;
  const outputFormat = resolveFormat(options.format);
  if (!outputFormat) {
    const raw = typeof options.format === "string" ? options.format : options.format.ext;
    throw new UnsupportedFormatError(raw, "output");
  }
  let inputFormat;
  if (options.inputFormat !== void 0) {
    inputFormat = resolveFormat(options.inputFormat);
    if (!inputFormat) {
      const raw = typeof options.inputFormat === "string" ? options.inputFormat : options.inputFormat.ext;
      throw new UnsupportedFormatError(raw, "input");
    }
  } else {
    const filenameHint = options.filename ?? (typeof File !== "undefined" && input instanceof File ? input.name : void 0);
    inputFormat = await detectFormatWithHint(input, filenameHint);
  }
  if (!inputFormat) {
    throw new UnsupportedFormatError(
      "(unknown)",
      "input",
      "Pass options.inputFormat or options.filename for text formats that have no magic bytes."
    );
  }
  const backend = await registry.findFor(inputFormat, outputFormat);
  if (!backend) {
    throw new NoBackendError(inputFormat.ext, outputFormat.ext);
  }
  const aligned = input.type === inputFormat.mime ? input : input.slice(0, input.size, inputFormat.mime);
  return backend.convert(aligned, outputFormat, options);
}

// src/convert-batch.ts
function toError(err) {
  return err instanceof Error ? err : new Error(String(err));
}
function abortError(signal) {
  return signal.reason instanceof Error ? signal.reason : new DOMException("Aborted", "AbortError");
}
function combineSignals(a, b) {
  if (!a) return b;
  if (!b) return a;
  const anyFn = AbortSignal.any;
  return typeof anyFn === "function" ? anyFn([a, b]) : a;
}
async function convertBatch(items, batchOptions = {}, context = {}) {
  const total = items.length;
  const results = new Array(total);
  if (total === 0) return results;
  const concurrency = Math.max(1, Math.floor(batchOptions.concurrency ?? 4));
  let completed = 0;
  let nextIndex = 0;
  const runWorker = async () => {
    for (; ; ) {
      const index = nextIndex++;
      if (index >= total) return;
      const item = items[index];
      if (!item) continue;
      let res;
      if (batchOptions.signal?.aborted) {
        res = { index, name: item.name, result: null, error: abortError(batchOptions.signal) };
      } else {
        const signal = combineSignals(batchOptions.signal, item.options.signal);
        const filename = item.options.filename ?? (typeof File !== "undefined" && item.input instanceof File ? item.input.name : void 0) ?? item.name;
        const itemOptions = signal === item.options.signal && filename === item.options.filename ? item.options : { ...item.options, signal, filename };
        try {
          const result = await convert(item.input, itemOptions, context);
          res = { index, name: item.name, result, error: null };
        } catch (err) {
          res = { index, name: item.name, result: null, error: toError(err) };
        }
      }
      results[index] = res;
      completed++;
      batchOptions.onItemComplete?.(res, completed, total);
    }
  };
  const workerCount = Math.min(concurrency, total);
  await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
  return results;
}

// src/lazy-wasm-loader.ts
function createLazyWasmLoader(config) {
  const { load, validate, LoadError, loadErrorMessage } = config;
  let _module = null;
  let _loading = null;
  let _generation = 0;
  async function doLoad() {
    try {
      const imported = await load();
      return validate(imported);
    } catch (err) {
      throw new LoadError(loadErrorMessage, { cause: err });
    }
  }
  async function ensureLoaded() {
    if (_module !== null) {
      return _module;
    }
    if (_loading !== null) {
      return _loading;
    }
    const myGen = ++_generation;
    _loading = doLoad().then((mod) => {
      if (myGen === _generation) {
        _module = mod;
      }
      return mod;
    });
    _loading.catch(() => {
      if (myGen === _generation) {
        _loading = null;
      }
    });
    return _loading;
  }
  function dispose() {
    _module = null;
    _loading = null;
    _generation++;
  }
  async function preload() {
    await ensureLoaded();
  }
  function getCached() {
    return _module;
  }
  return { ensureLoaded, dispose, preload, getCached };
}

export { BackendRegistry, EncodeNotImplementedError, InputTooLargeError, NoBackendError, RoundTripBackend, UnsupportedFormatError, WebcvtError, convert, convertBatch, createLazyWasmLoader, defaultRegistry, detectCapabilities, detectFormat, detectFormatWithHint, findByExt, findByMime, knownFormats, resolveFormat };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map